import { ClassAliases } from "@awayfl/avm2/dist/lib/amf";

/**
 * @description utility for using various versions of paths to navigate through the animate stage
 * @param path the string path to navigate, period separated
 * @param startingObject the export rood by defualt - used for relative tree navigation
 * @param childrenSearchField the field to search against in the children array - defaults to name.  Setting this falsable will not search through the children array
 * @returns the stage object found at the designated path.
 * @throws ProxyStageObjectNotFoundError
 */
export const navigateStage = (
  path: string | string[],
  startingObject: any = null,
  childrenSearchField: string = "name"
) => {
  // if nothing is passed, we assume the export root
  if (!path) {
    if (startingObject) return startingObject;
    return window["exportRoot"];
  }

  // navigate the path
  let pathList: string[] = [];

  if (Array.isArray(path)) {
    pathList = path;

    // if it wasnt an array then its a string, so check for dots
  } else if (path.indexOf(".") > -1) {
    pathList = path.split(".");
  } else {
    pathList = [path];
  }

  // if they want to use an absolute path go from the export root
  let currentStage;
  let pathSoFar = "";
  if (!startingObject) {
    currentStage = window["exportRoot"];
    pathSoFar = "exportRoot";
  } else {
    currentStage = startingObject;
  }

  // loop through and build the path
  // subject would be a good place to throw an error potentially
  pathList.forEach((pathItem) => {
    if (currentStage[pathItem]) {
      currentStage = currentStage[pathItem];
    } else {
      // check by name
      if (childrenSearchField && Array.isArray(currentStage["children"])) {
        // it has a children array so lets look through those fields
        for (let i = 0; i < currentStage["children"].length; i++) {
          // childrenSearchField defaults to name, in case a compat. issue arrises
          if (
            currentStage.children[i][childrenSearchField] &&
            currentStage.children[i][childrenSearchField] == pathItem
          ) {
            currentStage = currentStage.children[i];
            return;
          }
        }
      }

      throw new ProxyStageObjectNotFoundError(
        pathItem,
        pathSoFar,
        currentStage
      );
    }
    pathSoFar += "." + pathItem;
  });

  return currentStage;
};

// error specifically for
export class ProxyStageObjectNotFoundError extends Error {
  pathItem: string;
  pathSoFar: string;
  currentStage: [] | {};

  constructor(pathItem: string, pathSoFar: string, currentStage: [] | {}) {
    super(`Could not find stage object ${pathItem} in "${pathSoFar}"`);
    this.pathItem = pathItem;
    this.pathSoFar = pathSoFar;
    this.currentStage = currentStage;
  }
}

export const isFunction = (obj) => {
  return !!(obj && obj.constructor && obj.call && obj.apply);
};

// library method for writing the version of each part of the framework to the window
export const stampVersion = (label: string, version: string) => {
  //add the object if necessary
  if (!window["DIB_VERSIONS"]) {
    window["DIB_VERSIONS"] = {};
  }

  // see if our label is taken
  let finalLabel = label;
  let tries = 0;
  while (window["DIB_VERSIONS"][finalLabel]) {
    tries++;
    finalLabel = `${label} (${tries})`;
  }

  // attach the label
  window["DIB_VERSIONS"][finalLabel] = version;
};

export const getVersionString = (subject: Object, UpToClass) => {
  let versionString = "";
  if (subject.constructor["Version"]) {
    versionString += subject.constructor["Version"];
  } else {
    versionString += "No Version";
  }

  // dual purpose - if we ever get 10 layers deep this
  // framework has gone to hell anyway
  // but also no need risking
  // an infinite loop over debug data
  let loopCount = 0;

  // for debugging purposes in the future, we want to be able to see each version in the lineage
  let parentClass = subject.constructor;
  do {
    parentClass = Object.getPrototypeOf(parentClass);
    if (parentClass["Version"]) {
      versionString += ` > ${parentClass.name}@${parentClass["Version"]}`;
    }
    loopCount++;
  } while (parentClass != UpToClass && loopCount < 10);

  return versionString;
};

export const stampVersionString = (subject: Object, UpToClass) => {
  stampVersion(subject.constructor.name, getVersionString(subject, UpToClass));
};

export const doPassThrough = (canvasFrom, canvasTo) => {
  // const passThroughEvents = ['mousedown'] //['mouseout', 'mousedown', 'mousemove', 'mouseup'];
  const passThroughEvents = [
    "mouseout",
    "mousedown",
    "mousemove",
    "mouseup",
    "wheel",
  ];
  passThroughEvents.forEach((eventName) =>
    canvasFrom.addEventListener(eventName, (event) => {
      let passThrough = null;

      switch (eventName) {
        case "wheel":
        case "mousewheel":
          passThrough = new WheelEvent(eventName, {
            clientX: event.clientX,
            clientY: event.clientY,
            deltaY: event.deltaY,
          });
          break;

        default:
          passThrough = new MouseEvent(eventName, {
            clientX: event.clientX,
            clientY: event.clientY,
          });
      }

      canvasTo.dispatchEvent(passThrough);
    })
  );
};
